import type { Movement } from '../../types';
import { Clock, AlertTriangle } from 'lucide-react';
import { MovementCard } from '../List/MovementCard';
import { useState } from 'react';

export function DayFlowView({ movements }: { movements: Movement[] }) {
    const [detailLevel, setDetailLevel] = useState<'L1'|'L2'|'L3'>('L2');
    
    // Sort logically by time
    const sorted = [...movements].sort((a,b) => a.scheduledTime.localeCompare(b.scheduledTime));

    return (
        <div className="flex flex-col items-center pt-8 pb-32 w-full min-h-full">
            <div className="relative w-full max-w-4xl">
                
                {/* The Center Spine */}
                <div className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-0.5 bg-slate-200" />

                <div className="w-full justify-center flex py-4 z-20 sticky top-0 bg-slate-50/80 backdrop-blur border-b border-slate-200 mb-6 rounded-b">
                   <div className="flex items-center gap-1 bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
                        {(['L1', 'L2', 'L3'] as const).map(level => (
                           <button 
                             key={level}
                             onClick={() => setDetailLevel(level)}
                             className={`px-3 py-1.5 text-xs font-bold rounded-md transition-colors ${detailLevel === level ? 'bg-blue-100 text-blue-700' : 'text-slate-500 hover:bg-slate-50'}`}
                           >
                              {level === 'L1' ? 'L1 Overview' : level === 'L2' ? 'L2 Services' : 'L3 Stacking'}
                           </button>
                        ))}
                    </div>
                </div>

                {/* Simulated "NOW" Line injected dynamically between items based on time (mocked here at index 2) */}
                
                <div className="w-full relative z-0 flex flex-col gap-12 py-4">
                    {sorted.map((m, idx) => (
                        <div key={m.id} className="relative w-full flex flex-col justify-center">
                            
                            {/* Insert NOW Line mockup after 2nd item */}
                            {idx === 2 && (
                                <div className="absolute -top-10 left-0 right-0 flex items-center z-10 opacity-80 pointer-events-none">
                                    <div className="h-px bg-blue-500 flex-1"></div>
                                    <div className="px-3 py-1 rounded-full bg-blue-500 text-white text-[10px] font-bold tracking-widest flex items-center gap-1.5 shadow-sm">
                                        <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></div>
                                        NOW 10:45
                                    </div>
                                    <div className="h-px bg-blue-500 flex-1"></div>
                                </div>
                            )}

                            {/* Insert Conflict Alert Mockup after 3rd item */}
                            {idx === 3 && (
                                <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-20">
                                   <div className="bg-amber-50 border border-amber-200 shadow-md rounded-lg p-2 text-center w-64 flex flex-col items-center">
                                     <div className="flex items-center gap-1.5 text-amber-700 mb-0.5">
                                        <AlertTriangle size={14} className="animate-pulse" />
                                        <span className="font-bold text-[10px] tracking-wider uppercase">Crew Conflict</span>
                                     </div>
                                     <div className="text-amber-800 font-medium text-xs">14:00–15:00 &middot; 4 ops, 2 stacking</div>
                                   </div>
                                </div>
                            )}

                            <DayFlowRow movement={m} index={idx} isPast={idx < 2} detailLevel={detailLevel} />
                        </div>
                    ))}
                    
                    {/* End of Day Marker */}
                    <div className="relative w-full flex justify-center mt-12">
                       <div className="bg-white border-2 border-slate-200 rounded-full px-5 py-2 text-xs font-bold tracking-widest text-slate-400 uppercase shadow-sm z-10 flex items-center gap-2">
                           <Clock size={14} /> End of Day &middot; 17:00
                       </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

function DayFlowRow({ movement, isPast, detailLevel }: { movement: Movement, index: number, isPast: boolean, detailLevel: 'L1' | 'L2' | 'L3' }) {
    const isArrival = movement.direction === 'inbound';

    // Build the spine connector visually
    const NodeMarker = () => (
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-[11px] h-[11px] rounded-full bg-slate-400 border-[2.5px] border-white z-20 shadow-sm" />
    );

    const TimeLabel = ({ align }: { align: 'left' | 'right' }) => (
        <div className={`absolute top-1/2 -translate-y-1/2 ${align === 'left' ? 'right-[55%]' : 'left-[55%]'} text-xs font-bold font-mono text-slate-500 bg-white/90 px-1.5 py-0.5 rounded shadow-sm border border-slate-100`}>
            {movement.scheduledTime}
        </div>
    );

    return (
        <div className="relative w-full flex items-center justify-center">
            
            <NodeMarker />

            <div className="w-1/2 pr-12 flex justify-end relative items-center">
                {isArrival && (
                    <>
                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-12 border-t-2 border-slate-200 pointer-events-none" />
                        <TimeLabel align="left" />
                        
                        <div className={`w-[360px] relative z-30 ${isPast ? 'opacity-65 saturate-50 hover:opacity-100 hover:saturate-100 transition-all' : 'opacity-100'}`}>
                            <MovementCard movement={movement} detailLevel={detailLevel} compact={true} />
                        </div>
                    </>
                )}
            </div>
            
            <div className="w-1/2 pl-12 flex justify-start relative items-center">
                 {!isArrival && (
                    <>
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-12 border-t-2 border-slate-200 pointer-events-none" />
                        <TimeLabel align="right" />
                        
                        <div className={`w-[360px] relative z-30 ${isPast ? 'opacity-65 saturate-50 hover:opacity-100 hover:saturate-100 transition-all' : 'opacity-100'}`}>
                            <MovementCard movement={movement} detailLevel={detailLevel} compact={true} />
                        </div>
                    </>
                )}
            </div>

        </div>
    );
}

